import { memo, useCallback } from 'react';
import NextLink from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import NextImage from 'next/image';

import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { fade } from 'styles/animations';
import { toggleMobileNav } from 'store/slices/global';
import { useLogout } from 'hooks/useLogout';
import { HOME_VALUATION_ROUTE } from 'constants/routes';
import Text from '../common/text';
import { HeaderNavigationMobileWrapper } from './styles';
import { BLOG_LINK, MANIFESTO_LINK } from 'constants/links';
import { globalSelector } from 'store/slices/global/selector';
import { userSelector } from 'store/slices/auth/selector';
import useTriggerFunnelyticsEvent from 'hooks/useTriggerFunnelyticsEvent';
import {
  GENERIC_EVENTS,
  HOME_VALUATION_EVENTS,
} from 'hooks/useTriggerFunnelyticsEvent/constants';

function HeaderNavigationMobile() {
  const { logout } = useLogout();
  const { createEventTriggerer } = useTriggerFunnelyticsEvent();
  const { isOpenMobileNav } = useAppSelector(globalSelector);
  const { jwt } = useAppSelector(userSelector);

  const dispatch = useAppDispatch();

  const closeMobileMenu = useCallback(() => {
    dispatch(toggleMobileNav());
  }, [dispatch]);

  const onLogout = useCallback(() => {
    closeMobileMenu();
    logout();
  }, [logout, closeMobileMenu]);

  return (
    <HeaderNavigationMobileWrapper>
      <AnimatePresence>
        {isOpenMobileNav && (
          <motion.div
            variants={fade}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mobile-menu"
          >
            <div className="mobile-menu-close">
              <button
                type="button"
                onClick={closeMobileMenu}
                className="header-burger"
              >
                <NextImage
                  src="/static/icons/close.svg"
                  width={32}
                  height={32}
                  alt="Close Icon"
                />
              </button>
            </div>
            <div className="mobile-menu-logo">
              <NextImage
                src="/static/logo-richr-new.svg"
                width={142}
                height={48}
                priority
                quality={100}
              />
            </div>
            <div className="mobile-menu-content">
              <ul className="mobile-menu-list">
                <li>
                  <NextLink href={MANIFESTO_LINK} passHref>
                    <a target="_blank" onClick={closeMobileMenu}>
                      <Text type="mode10">Manifesto</Text>
                    </a>
                  </NextLink>
                </li>
                <li>
                  <NextLink href={BLOG_LINK} passHref>
                    <a target="_blank" onClick={closeMobileMenu}>
                      <Text type="mode10">Blog</Text>
                    </a>
                  </NextLink>
                </li>
              </ul>
              <a
                href="tel:+18449574247"
                className="mobile-menu-phone"
                onClick={createEventTriggerer({
                  eventName: GENERIC_EVENTS.CALL_EXPERT,
                  withPathname: true,
                  ctaPosition: 'mobile-nav-bar',
                })}
              >
                <Text fw="medium" type="mode9">
                  +1844-957-4247
                </Text>
              </a>
              <div className="mobile-menu-actions">
                <NextLink href={HOME_VALUATION_ROUTE} passHref>
                  <a
                    className="mobile-menu-actions-valuation"
                    onClick={createEventTriggerer({
                      eventName: HOME_VALUATION_EVENTS.START,
                      withPathname: true,
                      ctaPosition: 'mobile-nav-bar',
                    })}
                  >
                    <Text fw="bold">Get My Free Home Valuation</Text>
                  </a>
                </NextLink>
                {jwt && (
                  <button
                    type="button"
                    className="mobile-menu-actions-logout"
                    onClick={onLogout}
                  >
                    <Text fw="bold">Log out</Text>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </HeaderNavigationMobileWrapper>
  );
}

export default memo(HeaderNavigationMobile);
